import React, { useState, useEffect, useMemo, useContext } from 'react';
import { X, Download, Loader2, Upload, ImageIcon } from 'lucide-react';
import BBALoader from '../BBALoader';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import { useTheme } from '../../contexts/ThemeContext';
import { SidebarContext } from '../../contexts/SidebarContext';
import { getAccountById } from '../../services/firestoreService';
import { getMarketingFulfillmentData } from '../../services/marketingFulfillmentDataService';
import {
  TAILOR_TOKENS,
  COLOR_TOKENS,
  extractTokens,
  extractHexColors,
  applyTokens,
  buildTokenValues,
  wrapTailoredHtml,
} from '../../utils/hrTailorTokens';
import { getEnabledFormats, downloadTailored, imageUrlToDataUrl } from '../../utils/tailorDownloads';

const TOKEN_META = new Map(TAILOR_TOKENS.map((t) => [t.token, t]));

/**
 * Tailor modal: takes the template's HTML (pasted in CRM HR Collections, with
 * {{tokens}}), pre-fills each token from the client's account/profile data,
 * lets them adjust the values, live-previews the result, and downloads a PDF.
 */
const TailorTemplateModal = ({ isOpen, onClose, template, templateDoc, companyId, userData }) => {
  const { darkMode } = useTheme();
  // Fullscreen like DocumentViewerModal: flush against the portal sidebar.
  const sidebar = useContext(SidebarContext);
  const leftOffset = sidebar ? (sidebar.isCollapsed ? 80 : 256) : 0;
  const [values, setValues] = useState({});
  const [loadingAccount, setLoadingAccount] = useState(false);
  const [downloading, setDownloading] = useState(null); // format key while generating
  const [error, setError] = useState('');

  const html = templateDoc?.tailorHtml || '';
  const tokens = useMemo(() => extractTokens(html), [html]);
  const formats = useMemo(() => getEnabledFormats(templateDoc), [templateDoc]);

  // On open, pull the client's account record and seed every token value.
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setError('');
    setLoadingAccount(true);
    (async () => {
      let account = null;
      const brand = {};
      try {
        if (companyId) account = await getAccountById(companyId);
      } catch (e) {
        console.error('Error loading account for tailoring:', e);
      }
      // Brand hex codes live in the marketing fulfillment "Brand colors" free-text field.
      try {
        if (companyId) {
          const fulfillment = await getMarketingFulfillmentData(companyId);
          const hexes = extractHexColors(fulfillment?.website?.brandColors);
          if (hexes[0]) brand.primaryColor = hexes[0];
          if (hexes[1]) brand.secondaryColor = hexes[1];
        }
      } catch (e) {
        console.error('Error loading brand colors for tailoring:', e);
      }
      const vals = buildTokenValues(account || {}, userData || {}, brand);
      // Embed the profile logo as a data URL so it survives canvas capture
      // (PDF/PPTX) and renders in Word without CORS or network access. The
      // storage bucket blocks browser CORS, so fall back to the server-side
      // fetchImageAsDataUrl function when the direct fetch fails.
      if (vals.logoUrl && /^https?:/i.test(vals.logoUrl)) {
        let dataUrl = await imageUrlToDataUrl(vals.logoUrl);
        if (!dataUrl) {
          try {
            const fn = httpsCallable(functions, 'fetchImageAsDataUrl');
            const res = await fn({ url: vals.logoUrl });
            dataUrl = res.data?.dataUrl || null;
          } catch (e) {
            console.error('Server-side logo fetch failed:', e);
          }
        }
        if (dataUrl) vals.logoUrl = dataUrl;
      }
      if (!cancelled) {
        setValues(vals);
        setLoadingAccount(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, companyId, userData]);

  const renderedHtml = useMemo(() => applyTokens(html, values), [html, values]);

  // Escape to close + lock page scroll while the fullscreen tailor view is open.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, onClose]);

  if (!isOpen || !template) return null;

  const handleLogoFile = (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Please choose an image file for your logo.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Logo image must be under 5MB.');
      return;
    }
    setError('');
    const reader = new FileReader();
    reader.onload = () => setValues((prev) => ({ ...prev, logoUrl: reader.result }));
    reader.readAsDataURL(file);
  };

  const handleDownload = async (format) => {
    setDownloading(format.key);
    setError('');
    try {
      const companyPart = (values.companyName || '').trim();
      const namePart = template.name || template.title;
      await downloadTailored(format.key, {
        html: renderedHtml,
        fileName: [companyPart, namePart].filter(Boolean).join(' - '),
      });
    } catch (e) {
      console.error(`${format.label} download failed:`, e);
      setError(`Failed to generate the ${format.label}. Please try again.`);
    } finally {
      setDownloading(null);
    }
  };

  const inputClass = `w-full px-2.5 py-1.5 rounded-md border text-sm focus:outline-none focus:ring-2 focus:ring-red-500 ${
    darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'border-gray-300 text-gray-900 placeholder-gray-500'
  }`;

  return (
    <div
      className={`fixed inset-y-0 right-0 z-[100] flex flex-col transition-[left] duration-300 ${
        darkMode ? 'bg-gray-900' : 'bg-white'
      }`}
      style={{ left: leftOffset }}
    >
      {/* Header — slim single row */}
      <div className="flex items-center justify-between gap-3 px-5 py-2.5 border-b border-gray-200 dark:border-gray-700 shrink-0">
        <div className="min-w-0 flex items-baseline gap-2">
          <h2 className={`text-sm font-semibold whitespace-nowrap ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Tailor to Your Company
          </h2>
          <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
            {template.name || template.title}
          </span>
        </div>
        <button
          type="button"
          onClick={onClose}
          title="Close (Esc)"
          className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Body */}
      {!html.trim() ? (
        <div className="flex-1 flex items-center justify-center px-6 text-center">
          <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            This template isn't set up for tailoring yet. Please check back soon — our team is
            preparing it.
          </p>
        </div>
      ) : loadingAccount ? (
        <div className="flex-1 flex flex-col items-center justify-center gap-3 text-sm text-gray-500 dark:text-gray-400">
          <BBALoader size={40} />
          Loading your company details…
        </div>
      ) : (
        <div className="flex-1 min-h-0 flex flex-col lg:flex-row">
          {/* Live preview — the document fills the viewport, like the View button */}
          <div className="flex-1 min-h-0 order-2 lg:order-1">
            <iframe
              title="Tailored document preview"
              sandbox=""
              srcDoc={wrapTailoredHtml(renderedHtml)}
              className="w-full h-full border-0 bg-white"
            />
          </div>

          {/* Details + downloads — slim right panel */}
          <div
            className={`order-1 lg:order-2 w-full lg:w-72 xl:w-80 shrink-0 flex flex-col min-h-0 max-h-[45%] lg:max-h-none border-b lg:border-b-0 lg:border-l ${
              darkMode ? 'border-gray-700' : 'border-gray-200'
            }`}
          >
            {tokens.length > 0 && (
              <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3 space-y-2.5">
                {tokens.map((token) => {
                  const meta = TOKEN_META.get(token);
                  const isColor = COLOR_TOKENS.has(token);
                  const colorValue = /^#(?:[0-9a-fA-F]{6}|[0-9a-fA-F]{3})$/.test((values[token] || '').trim())
                    ? values[token].trim()
                    : '#000000';

                  if (token === 'logoUrl') {
                    const hasLogo = !!(values.logoUrl || '').trim();
                    return (
                      <div key={token}>
                        <label className={`block text-[11px] font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          Company logo
                        </label>
                        <div className="flex items-center gap-2.5">
                          <div
                            className={`w-20 h-12 shrink-0 rounded-md border flex items-center justify-center overflow-hidden bg-white ${
                              darkMode ? 'border-gray-600' : 'border-gray-300'
                            }`}
                          >
                            {hasLogo ? (
                              <img
                                src={values.logoUrl}
                                alt="Company logo"
                                className="max-w-[90%] max-h-[90%] object-contain"
                              />
                            ) : (
                              <span className="flex flex-col items-center gap-0.5 text-[10px] text-gray-400">
                                <ImageIcon className="w-4 h-4" />
                                No logo
                              </span>
                            )}
                          </div>
                          <div className="flex flex-col items-start gap-1">
                            <label
                              className={`cursor-pointer inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] font-medium border transition-colors ${
                                darkMode
                                  ? 'border-gray-600 text-gray-200 hover:bg-gray-700'
                                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                              }`}
                            >
                              <Upload className="w-3 h-3" />
                              {hasLogo ? 'Change logo' : 'Upload logo'}
                              <input type="file" accept="image/*" className="hidden" onChange={handleLogoFile} />
                            </label>
                            {hasLogo && (
                              <button
                                type="button"
                                onClick={() => setValues((prev) => ({ ...prev, logoUrl: '' }))}
                                className="text-[11px] text-gray-400 hover:text-red-500"
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div key={token}>
                      <label className={`block text-[11px] font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        {meta?.label || token}
                      </label>
                      <div className="flex items-center gap-1.5">
                        <input
                          type="text"
                          value={values[token] ?? ''}
                          onChange={(e) => setValues((prev) => ({ ...prev, [token]: e.target.value }))}
                          placeholder={isColor ? '#C8102E' : meta?.label || token}
                          className={inputClass}
                        />
                        {isColor && (
                          <input
                            type="color"
                            value={colorValue}
                            onChange={(e) => setValues((prev) => ({ ...prev, [token]: e.target.value }))}
                            title="Pick a color"
                            className="w-8 h-8 shrink-0 rounded-md border border-gray-300 dark:border-gray-600 cursor-pointer bg-transparent p-0.5"
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Downloads — pinned to the bottom of the panel */}
            <div className={`shrink-0 px-4 py-3 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              {error && (
                <p className="mb-2 text-sm text-red-600 dark:text-red-400 text-center">{error}</p>
              )}
              <div className="flex flex-col gap-2">
                {formats.map((format) => (
                  <button
                    key={format.key}
                    type="button"
                    onClick={() => handleDownload(format)}
                    disabled={!!downloading}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                  >
                    {downloading === format.key ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4" />
                    )}
                    {downloading === format.key ? 'Preparing…' : `Download ${format.portalLabel || format.label}`}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TailorTemplateModal;
